import type { StaffMember } from "@/lib/api/types";

/**
 * D153 — «الكابتن», the waiter who owns a dine-in table. The till's pure half.
 *
 * Read ONE bill at a time. Nothing here — and nothing that imports it — may
 * total, count, average, rank or order people by what happened on their
 * tables. The one ordering is the chip row's, which the server publishes as a
 * position (`captainOrder`: most recently captained first, then by name).
 */

/** The members who may be a captain, in the server's chip order. */
export function captainChips(staff: readonly StaffMember[]): StaffMember[] {
  return staff
    .filter(
      (member): member is StaffMember & { captainOrder: number } =>
        typeof member.captainOrder === "number",
    )
    .sort((a, b) => a.captainOrder - b.captainOrder);
}

/**
 * The name the floor uses: the first word of the member's display name —
 * «محمد علي» → «محمد». The guest knows whom to ask for, and the bill head stays
 * one short word. A name with nothing in it falls back to itself.
 */
export function captainFirstName(name: string): string {
  const first = name.trim().split(/\s+/)[0];
  return first || name;
}

/**
 * Is this session a WAITER's? It may open a bill but not present one — the
 * same test the server's default rule uses (`isWaiterSession`).
 */
export function isWaiterSession(permissions: readonly string[]): boolean {
  return (
    permissions.includes("RESTAURANT_INVOICE_OPEN") &&
    !permissions.includes("RESTAURANT_INVOICE_CLOSE")
  );
}

/**
 * The chip the table-open step shows SELECTED.
 *
 * Untouched, it shows the server's default and nothing else: the waiter's own
 * chip when the session is a waiter's and he is on the chip row, otherwise
 * nothing — never the cashier, never a guess. Touched, it shows the choice.
 */
export function shownCaptain(input: {
  touched: boolean;
  chosen: string | null;
  sessionSellerId: string | null;
  permissions: readonly string[];
  chips: readonly StaffMember[];
}): string | null {
  if (input.touched) return input.chosen;
  if (
    input.sessionSellerId !== null &&
    isWaiterSession(input.permissions) &&
    input.chips.some((chip) => chip.id === input.sessionSellerId)
  ) {
    return input.sessionSellerId;
  }
  return null;
}

/**
 * One tap on a captain chip: select it, or — tapping the selected one again —
 * clear it back to none, the covers chips' rule (skippable, and undoable
 * without opening anything).
 */
export function toggleCaptainChip(
  current: string | null,
  tapped: string,
): string | null {
  return current === tapped ? null : tapped;
}
